import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  IconButton,
  Divider,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  MenuItem,
  Collapse,
  CircularProgress,
  Checkbox,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
} from '@mui/material';
import { ArrowBack, Save, Add, Delete, Campaign, BookmarkAdd, BookmarkAdded } from '@mui/icons-material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createEventSchema } from '@utils/validators';
import { useAuth } from '@hooks/useAuth';
import { createEvent } from '@services/eventService';
import { logActivity, LOG_TYPES } from '@services/activityLogService';
import { sendEventAnnouncement } from '@services/notificationService';
import { Timestamp } from 'firebase/firestore';
import { saveTemplate, getTemplates, deleteTemplate } from '@services/templateService';

const CreateEventPage = () => {
  const navigate = useNavigate();
  const { userData: user } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [notifyVolunteers, setNotifyVolunteers] = useState(true);
  const [announceSnackbar, setAnnounceSnackbar] = useState('');

  // Template state
  const [templates, setTemplates] = useState([]);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateSnackbar, setTemplateSnackbar] = useState('');
  const [templateError, setTemplateError] = useState('');

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset,
    getValues,
  } = useForm({
    resolver: yupResolver(createEventSchema),
    defaultValues: {
      title: '',
      description: '',
      location: '',
      date: '',
      startTime: '',
      endTime: '',
      volunteersNeeded: 1,
      assignments: [
        { name: '', description: '', volunteersNeeded: 1, startTime: '', endTime: '' }
      ],
      isRecurring: false,
      recurrencePattern: '',
      monthlyWeekNumber: '',
      monthlyDayOfWeek: '',
      recurrenceEndDate: '',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'assignments',
  });

  // Watch assignments to calculate total volunteers
  const assignments = watch('assignments');
  const isRecurring = watch('isRecurring');
  const recurrencePattern = watch('recurrencePattern');

  // Load templates on mount
  useEffect(() => {
    if (user?.organizationId) {
      getTemplates(user.organizationId).then(setTemplates).catch(() => {});
    }
  }, [user?.organizationId]);

  const handleLoadTemplate = (template) => {
    reset({
      title: template.title,
      description: template.description,
      location: template.location,
      startTime: template.startTime,
      endTime: template.endTime,
      volunteersNeeded: template.volunteersNeeded,
      assignments: template.assignments.length > 0
        ? template.assignments.map(a => ({
            name: a.name || '',
            description: a.description || '',
            volunteersNeeded: a.volunteersNeeded || 1,
            startTime: a.startTime || '',
            endTime: a.endTime || '',
          }))
        : [{ name: '', description: '', volunteersNeeded: 1, startTime: '', endTime: '' }],
      // Keep date/recurrence fields blank — they're event-specific
      date: '',
      isRecurring: false,
      recurrencePattern: '',
      monthlyWeekNumber: '',
      monthlyDayOfWeek: '',
      recurrenceEndDate: '',
    });
    setLoadDialogOpen(false);
    setTemplateSnackbar(`Template "${template.name}" loaded.`);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      setSavingTemplate(true);
      setTemplateError('');
      const values = getValues();
      await saveTemplate(user.organizationId, templateName.trim(), values, user.uid);
      const updated = await getTemplates(user.organizationId);
      setTemplates(updated);
      setSaveDialogOpen(false);
      setTemplateName('');
      setTemplateSnackbar(`Template "${templateName.trim()}" saved.`);
    } catch (err) {
      console.error('Error saving template:', err);
      setTemplateError('Failed to save template. Please try again.');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (e, templateId) => {
    e.stopPropagation();
    await deleteTemplate(templateId);
    setTemplates((prev) => prev.filter((t) => t.id !== templateId));
  };

  // Helper to get the nth weekday of a month
  const getNthWeekdayOfMonth = (year, month, weekNumber, dayOfWeek) => {
    const dayMap = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
      thursday: 4, friday: 5, saturday: 6
    };
    const targetDay = dayMap[dayOfWeek];

    if (weekNumber === 'last') {
      // Start from the last day of the month and work backwards
      const lastDay = new Date(year, month + 1, 0);
      while (lastDay.getDay() !== targetDay) {
        lastDay.setDate(lastDay.getDate() - 1);
      }
      return lastDay;
    } else {
      // Find the nth occurrence
      const weekMap = { first: 1, second: 2, third: 3, fourth: 4 };
      const occurrence = weekMap[weekNumber];

      // Start from the first day of the month
      const firstDay = new Date(year, month, 1);
      let count = 0;

      for (let day = 1; day <= 31; day++) {
        const date = new Date(year, month, day);
        if (date.getMonth() !== month) break; // Month overflow

        if (date.getDay() === targetDay) {
          count++;
          if (count === occurrence) {
            return date;
          }
        }
      }
    }
    return null;
  };

  // Parse a YYYY-MM-DD string or Date object as a local date (avoids UTC-midnight timezone shift)
  const parseLocalDate = (dateInput) => {
    if (dateInput instanceof Date) return new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate());
    const [year, month, day] = dateInput.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Generate recurring event dates
  const generateRecurringDates = (startDate, pattern, endDate, monthlyWeekNumber, monthlyDayOfWeek) => {
    const dates = [];
    let currentDate = parseLocalDate(startDate);
    const finalDate = parseLocalDate(endDate);

    // Add the first occurrence
    dates.push(new Date(currentDate));

    while (currentDate < finalDate) {
      switch (pattern) {
        case 'weekly':
          currentDate.setDate(currentDate.getDate() + 7);
          break;
        case 'biweekly':
          currentDate.setDate(currentDate.getDate() + 14);
          break;
        case 'monthly':
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
        case 'monthly-weekday':
          // Move to next month
          let nextMonth = currentDate.getMonth() + 1;
          let nextYear = currentDate.getFullYear();
          if (nextMonth > 11) {
            nextMonth = 0;
            nextYear++;
          }

          // Find the nth weekday in the next month
          currentDate = getNthWeekdayOfMonth(nextYear, nextMonth, monthlyWeekNumber, monthlyDayOfWeek);
          if (!currentDate) return dates; // Safety check
          break;
        default:
          return dates;
      }

      if (currentDate <= finalDate) {
        dates.push(new Date(currentDate));
      }
    }

    return dates;
  };

  const onSubmit = async (data) => {
    try {
      console.log('Form submitted with data:', data);
      setError('');
      setLoading(true);

      // Process assignments to ensure proper data types
      const processedAssignments = data.assignments.map(assignment => ({
        name: assignment.name,
        description: assignment.description || '',
        volunteersNeeded: parseInt(assignment.volunteersNeeded),
        volunteersAssigned: 0,
        startTime: assignment.startTime || null,
        endTime: assignment.endTime || null,
      }));

      // Generate event dates (single or multiple for recurring)
      let eventDates = [];

      if (data.isRecurring && data.recurrencePattern && data.recurrenceEndDate) {
        // Generate recurring dates
        eventDates = generateRecurringDates(
          data.date,
          data.recurrencePattern,
          data.recurrenceEndDate,
          data.monthlyWeekNumber,
          data.monthlyDayOfWeek
        );
      } else {
        // Single event
        eventDates = [parseLocalDate(data.date)];
      }

      // Generate a unique recurrence ID if this is recurring
      const recurrenceId = data.isRecurring ? `recur_${Date.now()}` : null;

      // Create all event instances
      const createPromises = eventDates.map((eventDate, index) => {
        const [startHours, startMinutes] = data.startTime.split(':');
        eventDate.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

        const eventData = {
          title: data.title,
          description: data.description,
          location: data.location,
          date: Timestamp.fromDate(eventDate),
          startTime: data.startTime,
          endTime: data.endTime,
          volunteersNeeded: parseInt(data.volunteersNeeded),
          assignments: processedAssignments.map(a => ({ ...a })), // Clone assignments for each instance
          organizationId: user.organizationId,
          organizationName: user.organizationName,
          createdBy: user.uid,
          isRecurring: data.isRecurring,
          recurrenceId: recurrenceId,
          recurrencePattern: data.recurrencePattern || null,
          recurrenceEndDate: data.recurrenceEndDate || null,
          monthlyWeekNumber: data.monthlyWeekNumber || null,
          monthlyDayOfWeek: data.monthlyDayOfWeek || null,
          instanceNumber: index + 1,
          totalInstances: eventDates.length,
        };

        return createEvent(eventData);
      });

      console.log(`Creating ${createPromises.length} event(s)...`);
      const results = await Promise.all(createPromises);
      console.log('Events created successfully:', results);

      // Log activity for each created event
      results.forEach((eventId, i) => {
        logActivity({
          organizationId: user.organizationId,
          organizationName: user.organizationName,
          type: LOG_TYPES.EVENT_CREATED,
          actorId: user.uid,
          actorName: user.displayName,
          targetId: eventId,
          targetName: data.title,
        });
      });

      // Send volunteer announcement email if requested (use first event ID)
      if (notifyVolunteers && results.length > 0) {
        try {
          const { sent } = await sendEventAnnouncement(results[0]);
          setAnnounceSnackbar(`Announcement sent to ${sent} volunteer${sent !== 1 ? 's' : ''}.`);
        } catch (announceErr) {
          console.warn('Announcement email failed:', announceErr.message);
          // Non-fatal — event was created successfully regardless
        }
      }

      // Small delay if showing snackbar, then navigate
      setTimeout(() => navigate('/dashboard'), notifyVolunteers ? 1500 : 0);
    } catch (err) {
      console.error('Error creating event:', err);
      console.error('Error stack:', err.stack);
      setError(err.message || 'Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if user data is loaded
  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          Loading user data...
        </Alert>
      </Container>
    );
  }

  if (!user.organizationId) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          You must be associated with an organization to create events.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          variant="outlined"
        >
          Back to Dashboard
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Create New Event
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Fill in the details below to create a new volunteer event for your organization
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              startIcon={<BookmarkAdded />}
              onClick={() => setLoadDialogOpen(true)}
              size="small"
              disabled={templates.length === 0}
            >
              Load Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<BookmarkAdd />}
              onClick={() => { setTemplateName(''); setSaveDialogOpen(true); }}
              size="small"
            >
              Save as Template
            </Button>
          </Box>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit, (errors) => {
          console.error('Form validation errors:', errors);
          setError('Please fix the form errors before submitting.');
        })} noValidate>
          {/* Event Name */}
          <TextField
            fullWidth
            label="Event Name"
            margin="normal"
            {...register('title')}
            error={!!errors.title}
            helperText={errors.title?.message}
            disabled={loading}
            placeholder="e.g., Community Beach Cleanup"
            InputLabelProps={{ shrink: true }}
          />

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={4}
            {...register('description')}
            error={!!errors.description}
            helperText={errors.description?.message}
            disabled={loading}
            placeholder="Describe the event, what volunteers will be doing, and any important details..."
            InputLabelProps={{ shrink: true }}
          />

          {/* Location */}
          <TextField
            fullWidth
            label="Location"
            margin="normal"
            {...register('location')}
            error={!!errors.location}
            helperText={errors.location?.message}
            disabled={loading}
            placeholder="e.g., Santa Monica Beach, 123 Ocean Ave"
            InputLabelProps={{ shrink: true }}
          />

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Event Date"
                type="date"
                {...register('date')}
                error={!!errors.date}
                helperText={errors.date?.message}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Volunteers Needed */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Volunteers Needed"
                type="number"
                {...register('volunteersNeeded')}
                error={!!errors.volunteersNeeded}
                helperText={errors.volunteersNeeded?.message}
                disabled={loading}
                inputProps={{ min: 1 }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Start Time */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                {...register('startTime')}
                error={!!errors.startTime}
                helperText={errors.startTime?.message}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* End Time */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                {...register('endTime')}
                error={!!errors.endTime}
                helperText={errors.endTime?.message}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>

          {/* Recurrence Section */}
          <Box sx={{ mt: 3 }}>
            <Controller
              name="isRecurring"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      disabled={loading}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        Recurring Event
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Create multiple occurrences of this event automatically
                      </Typography>
                    </Box>
                  }
                />
              )}
            />

            <Collapse in={isRecurring}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Repeat Pattern"
                    {...register('recurrencePattern')}
                    error={!!errors.recurrencePattern}
                    helperText={errors.recurrencePattern?.message}
                    disabled={loading}
                    defaultValue=""
                  >
                    <MenuItem value="weekly">Weekly (Every week)</MenuItem>
                    <MenuItem value="biweekly">Biweekly (Every 2 weeks)</MenuItem>
                    <MenuItem value="monthly">Monthly (Same date each month)</MenuItem>
                    <MenuItem value="monthly-weekday">Monthly (Same weekday)</MenuItem>
                  </TextField>
                </Grid>

                {/* Show week/day selectors for monthly-weekday pattern */}
                {recurrencePattern === 'monthly-weekday' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label="Which Week"
                        {...register('monthlyWeekNumber')}
                        error={!!errors.monthlyWeekNumber}
                        helperText={errors.monthlyWeekNumber?.message}
                        disabled={loading}
                        defaultValue=""
                      >
                        <MenuItem value="first">First</MenuItem>
                        <MenuItem value="second">Second</MenuItem>
                        <MenuItem value="third">Third</MenuItem>
                        <MenuItem value="fourth">Fourth</MenuItem>
                        <MenuItem value="last">Last</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label="Day of Week"
                        {...register('monthlyDayOfWeek')}
                        error={!!errors.monthlyDayOfWeek}
                        helperText={errors.monthlyDayOfWeek?.message}
                        disabled={loading}
                        defaultValue=""
                      >
                        <MenuItem value="sunday">Sunday</MenuItem>
                        <MenuItem value="monday">Monday</MenuItem>
                        <MenuItem value="tuesday">Tuesday</MenuItem>
                        <MenuItem value="wednesday">Wednesday</MenuItem>
                        <MenuItem value="thursday">Thursday</MenuItem>
                        <MenuItem value="friday">Friday</MenuItem>
                        <MenuItem value="saturday">Saturday</MenuItem>
                      </TextField>
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Repeat Until"
                    type="date"
                    {...register('recurrenceEndDate')}
                    error={!!errors.recurrenceEndDate}
                    helperText={errors.recurrenceEndDate?.message || 'Last occurrence date (max 1 year)'}
                    disabled={loading}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Box>

          {/* Assignments Section */}
          <Divider sx={{ my: 4 }} />

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Volunteer Assignments
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create specific roles or tasks that volunteers can sign up for
                </Typography>
              </Box>
              <Button
                startIcon={<Add />}
                onClick={() => append({ name: '', description: '', volunteersNeeded: 1, startTime: '', endTime: '' })}
                disabled={loading}
                variant="outlined"
              >
                Add Assignment
              </Button>
            </Box>

            {errors.assignments && !Array.isArray(errors.assignments) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.assignments.message}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {fields.map((field, index) => (
                <Card key={field.id} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Assignment {index + 1}
                      </Typography>
                      {fields.length > 1 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => remove(index)}
                          disabled={loading}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Assignment Name"
                          {...register(`assignments.${index}.name`)}
                          error={!!errors.assignments?.[index]?.name}
                          helperText={errors.assignments?.[index]?.name?.message}
                          disabled={loading}
                          placeholder="e.g., Setup Team, Registration Desk"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Volunteers Needed"
                          type="number"
                          {...register(`assignments.${index}.volunteersNeeded`)}
                          error={!!errors.assignments?.[index]?.volunteersNeeded}
                          helperText={errors.assignments?.[index]?.volunteersNeeded?.message}
                          disabled={loading}
                          inputProps={{ min: 1 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Description (Optional)"
                          multiline
                          rows={2}
                          {...register(`assignments.${index}.description`)}
                          error={!!errors.assignments?.[index]?.description}
                          helperText={errors.assignments?.[index]?.description?.message || 'Describe what this assignment involves'}
                          disabled={loading}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Assignment Start Time (Optional)"
                          type="time"
                          {...register(`assignments.${index}.startTime`)}
                          InputLabelProps={{ shrink: true }}
                          disabled={loading}
                          helperText="When this assignment starts within the event"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Assignment End Time (Optional)"
                          type="time"
                          {...register(`assignments.${index}.endTime`)}
                          InputLabelProps={{ shrink: true }}
                          disabled={loading}
                          helperText="When this assignment ends"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* Notify volunteers option */}
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={notifyVolunteers}
              onChange={(e) => setNotifyVolunteers(e.target.checked)}
              disabled={loading}
              color="primary"
            />
            <Box>
              <Typography variant="body2" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Campaign fontSize="small" color="primary" />
                Notify all volunteers by email
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Sends an announcement email to all active volunteers in your organization
              </Typography>
            </Box>
          </Box>

          {/* Submit Button */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
            >
              {loading ? 'Creating Event...' : 'Create Event'}
            </Button>
          </Box>
        </Box>
      </Paper>
      <Snackbar
        open={!!announceSnackbar}
        onClose={() => setAnnounceSnackbar('')}
        message={announceSnackbar}
        autoHideDuration={4000}
      />
      <Snackbar
        open={!!templateSnackbar}
        onClose={() => setTemplateSnackbar('')}
        message={templateSnackbar}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Load Template Dialog */}
      <Dialog open={loadDialogOpen} onClose={() => setLoadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Load Template</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {templates.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No templates saved yet.</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {templates.map((t, idx) => (
                <Box key={t.id}>
                  {idx > 0 && <Divider />}
                  <ListItem
                    button
                    onClick={() => handleLoadTemplate(t)}
                    sx={{ pr: 7 }}
                  >
                    <ListItemText
                      primary={t.name}
                      secondary={[
                        t.title && `"${t.title}"`,
                        t.location && t.location,
                        t.assignments?.length && `${t.assignments.length} assignment${t.assignments.length !== 1 ? 's' : ''}`,
                      ].filter(Boolean).join(' · ')}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Delete template">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => handleDeleteTemplate(e, t.id)}
                        >
                          <Delete fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Box>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => { setSaveDialogOpen(false); setTemplateError(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>Save as Template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Saves the current form (title, description, location, times, and assignments) as a reusable template. Date and recurrence settings are not saved.
          </Typography>
          {templateError && <Alert severity="error" sx={{ mb: 2 }}>{templateError}</Alert>}
          <TextField
            label="Template name"
            placeholder="e.g., Food Drive Setup"
            fullWidth
            autoFocus
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSaveTemplate()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={savingTemplate || !templateName.trim()}
            startIcon={savingTemplate ? <CircularProgress size={16} /> : <BookmarkAdd />}
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreateEventPage;
